/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/no-array-index-key */
import React, { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import CourseCard from './course-card-square';
import { getOrganizationPropertiesByHost } from '../../../utils/helpers';
import ArrowRight from '../../../../public/assets/white-next.svg';
import GreyNext from '../../../../public/assets/grey-next.svg';
import GET_COURSE_BATCH_BY_FILTER from '../../../gql/queries/course-group-batch-filter.gql';
import MapSvg from '../../home/icons/map.svg';
import NoDataFound from '../../shared/NoDataFound';
import BatchCardSkeleton from '../../shared/batchCardSkeleton';
import { useMiscellaneousData } from '../../../contexts/ssr-miscellaneous-context';

/**
 * @param {Array} batches - Array of batch objects
 * @returns {Array} - Sorted array
 */
const sortBatchesByDate = (batches) => {
  if (!Array.isArray(batches) || batches.length === 0) {
    return batches;
  }

  const sorted = [...batches];

  const getTimestamp = (commencementDate) => (
    commencementDate ? new Date(commencementDate).getTime() : Infinity
  );

  sorted.sort((a, b) => getTimestamp(a?.commencementDate) - getTimestamp(b?.commencementDate));

  return sorted;
};

export default function CourseBatchSection({
  title,
  subtitle,
  sectionTitle,
  courses = [],
  // combos = [],
  notes,
  filterList,
  tagList,
  sectionId = '',
  courseGroupId,
  className = '',
}) {
  const [selectedTab, setSelectedTab] = useState('');
  const [filteredCourses, setFilteredCourses] = useState(courses);
  const [filteredCombos, setFilteredCombos] = useState([]);

  const streamOptions = Array.isArray(filterList)
    ? (filterList.find((f) => f?.key === 'streamId')?.options || [])
    : [];
  const centreOptions = Array.isArray(filterList)
    ? (filterList.find((f) => f?.key === 'centreId')?.options || [])
    : [];
  const examOptions = Array.isArray(filterList)
    ? (filterList.find((f) => f?.key === 'examId')?.options || [])
    : [];
  const courseTagsOptions = Array.isArray(tagList) ? tagList : [];
  const [filters, setFilters] = useState([]);
  const { miscellaneousData } = useMiscellaneousData();
  const currentOrg = getOrganizationPropertiesByHost(miscellaneousData?.hostname);
  const orgName = currentOrg?.orgName || currentOrg?.organizationName;
  const isMadeeasy = orgName === 'madeeasyprime' || orgName === 'madeeasy';
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isStreamOpen, setIsStreamOpen] = useState(false);
  const [isCentreOpen, setIsCentreOpen] = useState(false);
  const [isCourseTagsOpen, setIsCourseTagsOpen] = useState(false);
  const [selectedStreamName, setSelectedStreamName] = useState('Select Stream');
  const [selectedCentreName, setSelectedCentreName] = useState('Select Centre');
  const [selectedComboId, setSelectedComboId] = useState('');
  const [selectedCourseTags, setSelectedCourseTags] = useState(courseTagsOptions.length > 0 ? courseTagsOptions[0].name : 'Select Course');
  const showVenue = courses[0]?.venue;

  const checkScroll = () => {
    if (!scrollRef.current || filteredCourses.length === 0) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    if (scrollWidth > 0 && clientWidth > 0) {
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };
  const { data, loading } = useQuery(GET_COURSE_BATCH_BY_FILTER, {
    variables: {
      courseGroupId,
      sectionId,
      filters,
      comboProductId: !isMadeeasy && selectedComboId ? selectedComboId : null,
    },
    skip: isMadeeasy ? filters.length === 0 : (filters.length === 0 && !selectedComboId),
  });

  useEffect(() => {
    if (courses.length > 0) {
      setFilteredCourses(sortBatchesByDate(courses));
    }

    // if (combos.length > 0) {
    //   setFilteredCombos(combos);
    // }
  }, [courses]);

  useEffect(() => {
    if (data?.getCourseGroupSectionByFilter?.batches) {
      setFilteredCourses(sortBatchesByDate(data?.getCourseGroupSectionByFilter?.batches));
    }

    if (data?.getCourseGroupSectionByFilter?.combos) {
      setFilteredCombos(data?.getCourseGroupSectionByFilter?.combos || []);
    }
  }, [data]);

  useEffect(() => {
    // Add a small delay to ensure DOM is fully rendered with the new data
    const timer = setTimeout(() => {
      checkScroll();
    }, 100);

    // Additional check after a longer delay for images/content to load
    const longerTimer = setTimeout(() => {
      checkScroll();
    }, 500);

    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
    }

    // Add window resize listener to recalculate scroll state
    const handleResize = () => {
      setTimeout(() => {
        checkScroll();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      clearTimeout(longerTimer);
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [filteredCourses]);

  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    const { firstChild } = scrollRef.current;
    const cardWidth = firstChild?.offsetWidth || 358;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const itemsPerScroll = isMobile ? 1 : 3;
    const scrollAmount = isMobile ? cardWidth * itemsPerScroll + 20 : cardWidth * itemsPerScroll + 75;
    if (direction === 'left') {
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const exists = prev.find((filter) => filter.key === key);
      if (exists) {
        if (!value) {
          return prev.filter((filter) => filter.key !== key);
        }
        return prev.map((filter) => (filter.key === key ? { key, value } : filter));
      }
      return value ? [...prev, { key, value }] : prev;
    });
  };

  const handleTabClick = (tab) => {
    setSelectedTab(tab.name);
    updateFilter('examId', tab?.id || '');
  };

  const handleComboClick = (combo) => {
    if (selectedComboId === combo.id) {
      setSelectedTab('');
      setSelectedComboId('');
    } else {
      setSelectedTab(combo.name);
      setSelectedComboId(combo.id || '');
    }
  };

  const handleSelectStream = (stream) => {
    setSelectedStreamName(stream?.name);
    setIsStreamOpen(false);
    updateFilter('streamId', stream?.id || '');
  };

  const handleSelectCentre = (centre) => {
    setSelectedCentreName(centre?.name);
    setIsCentreOpen(false);
    updateFilter('centreId', centre?.id || '');
  };

  const handleSelectCourseTags = (tag) => {
    setSelectedComboId('');
    setSelectedCourseTags(tag?.name);
    setIsCourseTagsOpen(false);
    updateFilter('tagId', tag?.id || '');
  };

  useEffect(() => {
    const fragment = window.location.hash.replace('#', '');
    if (fragment === '2-year-elevate') {
      const elevateTag = courseTagsOptions.find((tag) => tag.name.toLowerCase().includes('elevate'));
      if (elevateTag) {
        handleSelectCourseTags(elevateTag);
        return;
      }
    }

    if (courseTagsOptions.length > 0) {
      handleSelectCourseTags(courseTagsOptions[0]);
    }
  }, [courseTagsOptions]);

  useEffect(() => {
    if (!isMadeeasy && filteredCombos.length > 0 && selectedComboId === '') {
      const firstCombo = filteredCombos[0];
      setSelectedTab(firstCombo.name);
      setSelectedComboId(firstCombo.id || '');
    }
  }, [filteredCombos]);

  if (!isMadeeasy && courseTagsOptions.length === 0) {
    return null;
  }

  return (
    <div className={`w-full flex mx-auto bg-[#d2e8ff30] flex-col items-stretch px-5 md:px-[52px] ${className}`}>
      {(title || subtitle)
      && (
      <div className="w-full flex flex-col items-center text-center mb-1">
        {title && <div dangerouslySetInnerHTML={{ __html: title }} />}
        {subtitle && <div dangerouslySetInnerHTML={{ __html: subtitle }} />}
      </div>
      )}
      <div className="w-full flex flex-col gap-4 sm:gap-6">
        <div className="flex flex-col w-full gap-2">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-3">
            <p className="text-[24px] font-medium text-black">
              {sectionTitle}
            </p>

            <div className="flex gap-1 items-stretch sm:items-center w-full lg:w-auto">
              {currentOrg?.organizationName === 'madeeasyprime' && streamOptions.length > 0 && (
              <div className="relative w-full sm:w-52">
                <button
                  type="button"
                  onClick={() => setIsStreamOpen((prev) => !prev)}
                  className="flex items-center justify-between w-full px-3 sm:px-4 py-2 sm:py-[10px] bg-white border border-thinBlue rounded-lg shadow-sm"
                >
                  <span className="text-gray-700 text-xs sm:text-sm max-w-[150px] truncate block">{selectedStreamName}</span>
                  <svg width="14" height="7" viewBox="0 0 14 7" className={` text-customBlue transition-transform ${isStreamOpen ? 'rotate-180' : ''}`} fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.707108 1.09218C0.316607 1.41761 0.316607 1.94527 0.707107 2.27069L5.59941 6.34352C6.3805 6.99386 7.6463 6.99361 8.4271 6.34302L13.3175 2.26769C13.708 1.94227 13.708 1.41461 13.3175 1.08917C12.9269 0.763732 12.2938 0.763732 11.9032 1.08917L7.7176 4.57719C7.3271 4.90269 6.6939 4.90269 6.3034 4.57719L2.12131 1.09218C1.73081 0.76674 1.09771 0.76674 0.707108 1.09218Z" fill="#217ABB" />
                  </svg>
                </button>
                {isStreamOpen && (
                <ul className="absolute z-10 mt-2 w-full bg-white border border-blueThinDobule rounded-lg shadow-md max-h-60 overflow-auto border-b-[4px] border-b-[#00C3FF]">
                  {streamOptions.map((stream) => (
                    <li
                      key={stream?.id}
                      onClick={() => handleSelectStream(stream)}
                      className={`px-4 py-2 m-2 cursor-pointer hover:bg-blue-50 border-b ${filters.find((filter) => filter.value === 'streamId')?.key === stream.id ? 'bg-lightThinBlue text-customBlue font-semibold' : 'text-gray-700 '}`}
                    >
                      {stream?.name}
                    </li>
                  ))}
                </ul>
                )}
              </div>
              )}

              {isMadeeasy && showVenue && centreOptions.length > 0 && (
              <div className="relative w-full sm:w-52">
                <button
                  type="button"
                  onClick={() => setIsCentreOpen((prev) => !prev)}
                  className="flex items-center justify-between w-full px-3 sm:px-4 py-2 sm:py-[10px] bg-white border border-thinBlue rounded-lg shadow-sm"
                >
                  <div className="flex gap-2">
                    <Image width={18} height={18} src={MapSvg} className="text-blue-500" alt="mapicons" />
                    <span className="text-gray-700 text-xs sm:text-sm max-w-[150px] truncate block">{selectedCentreName}</span>
                  </div>
                  <svg width="14" height="7" viewBox="0 0 14 7" className={` text-customBlue transition-transform ${isCentreOpen ? 'rotate-180' : ''}`} fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.707108 1.09218C0.316607 1.41761 0.316607 1.94527 0.707107 2.27069L5.59941 6.34352C6.3805 6.99386 7.6463 6.99361 8.4271 6.34302L13.3175 2.26769C13.708 1.94227 13.708 1.41461 13.3175 1.08917C12.9269 0.763732 12.2938 0.763732 11.9032 1.08917L7.7176 4.57719C7.3271 4.90269 6.6939 4.90269 6.3034 4.57719L2.12131 1.09218C1.73081 0.76674 1.09771 0.76674 0.707108 1.09218Z" fill="#217ABB" />
                  </svg>
                </button>
                {isCentreOpen && (
                <ul className="absolute z-10 mt-2 w-full bg-white border border-blueThinDobule rounded-lg shadow-md max-h-60 overflow-auto border-b-[4px] border-b-[#00C3FF]">
                  {centreOptions.map((centre) => (
                    <li
                      key={centre.id}
                      onClick={() => handleSelectCentre(centre)}
                      className={`px-4 py-2 m-2 cursor-pointer hover:bg-blue-50 border-b ${filters.find((filter) => filter.value === 'centreId')?.key === centre?.id ? 'bg-lightThinBlue text-customBlue font-semibold' : 'text-gray-700 '}`}
                    >
                      {centre.name}
                    </li>
                  ))}
                </ul>
                )}
              </div>
              )}

              { courseTagsOptions.length > 0 && (
                <div className="relative w-full sm:w-52">
                  <button
                    type="button"
                    onClick={() => setIsCourseTagsOpen((prev) => !prev)}
                    className="flex items-center justify-between w-full px-3 sm:px-4 py-2 sm:py-[10px] bg-white border border-thinBlue rounded-lg shadow-sm"
                  >
                    <span className="text-gray-700 text-xs sm:text-sm max-w-[150px] truncate block">{selectedCourseTags}</span>
                    <svg width="14" height="7" viewBox="0 0 14 7" className={` text-customBlue transition-transform ${isCourseTagsOpen ? 'rotate-180' : ''}`} fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.707108 1.09218C0.316607 1.41761 0.316607 1.94527 0.707107 2.27069L5.59941 6.34352C6.3805 6.99386 7.6463 6.99361 8.4271 6.34302L13.3175 2.26769C13.708 1.94227 13.708 1.41461 13.3175 1.08917C12.9269 0.763732 12.2938 0.763732 11.9032 1.08917L7.7176 4.57719C7.3271 4.90269 6.6939 4.90269 6.3034 4.57719L2.12131 1.09218C1.73081 0.76674 1.09771 0.76674 0.707108 1.09218Z" fill="#217ABB" />
                    </svg>
                  </button>
                  {isCourseTagsOpen && (
                  <ul className="absolute z-10 mt-2 w-full bg-white border border-blueThinDobule rounded-lg shadow-md max-h-60 overflow-auto border-b-[4px] border-b-[#00C3FF]">
                    {courseTagsOptions.map((tag) => (
                      <li
                        key={tag.id}
                        onClick={() => handleSelectCourseTags(tag)}
                        className={`px-4 py-2 m-2 cursor-pointer hover:bg-blue-50 border-b ${filters.find((filter) => filter.value === 'tagId')?.key === tag?.id ? 'bg-lightThinBlue text-customBlue font-semibold' : 'text-gray-700 '}`}
                      >
                        {tag.name}
                      </li>
                    ))}
                  </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          {isMadeeasy && examOptions.length > 0 && (
            <div className="flex gap-2 mt-3 md:mt-0 whitespace-nowrap overflow-x-auto">
              {examOptions.map((tab) => (
                <div
                  key={tab?.name}
                  className={`flex items-center gap-6 border border-[#E1192B] py-1 px-2 rounded-md ${
                    selectedTab === tab?.name
                      ? 'bg-[#E1192B] text-white font-semibold'
                      : 'bg-white text-[#E1192B]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleTabClick(tab)}
                    className="text-xs sm:text-sm"
                  >
                    {tab?.name}
                  </button>
                  {/* {i < examOptions.length - 1 && (
                    <div className="w-px h-3 sm:h-4 bg-[#b9b9b9]" />
                  )} */}
                </div>
              ))}
            </div>
          )}

          {!isMadeeasy && filteredCombos.length > 0 && (
            <div className="flex gap-2 mt-3 md:mt-0 whitespace-nowrap overflow-x-auto">
              {filteredCombos.map((combo) => (
                <div
                  key={combo?.id}
                  className={`flex items-center gap-6 border border-[#E1192B] py-1 px-2 rounded-md ${
                    selectedTab === combo?.name
                      ? 'bg-[#E1192B] text-white font-semibold'
                      : 'bg-white text-[#E1192B]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleComboClick(combo)}
                    className="text-xs sm:text-sm"
                  >
                    {combo?.name}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {!loading && filteredCourses.length === 0 && isMadeeasy && (
          <NoDataFound noMsg="No Batches Found" />
        )}

        <div className="relative w-full flex justify-center">
          <div className="relative w-full">
            {!loading && filteredCourses.length > 3 && (
              <button
                type="button"
                onClick={() => handleScroll('left')}
                disabled={!canScrollLeft}
                className={`hidden md:inline-flex absolute -left-6 top-1/2 -translate-y-1/2 shadow p-3 z-10 rounded-lg ${
                  canScrollLeft ? 'bg-[#0267b1]' : 'bg-white'
                }`}
              >
                <Image
                  src={canScrollLeft ? ArrowRight : GreyNext}
                  alt="Left Arrow"
                  className="w-4 h-4 rotate-180"
                />
              </button>
            )}

            <div
              ref={scrollRef}
              className="flex gap-6 overflow-x-auto md:overflow-x-hidden scroll-smooth snap-x md:snap-none w-full"
            >
              {loading
                ? Array.from({ length: 3 }).map((_, idx) => (
                  <div
                    key={`skeleton-${idx}`}
                    className="flex-shrink-0 w-full md:w-[438px] md:max-w-[438px] snap-center md:snap-start"
                  >
                    <BatchCardSkeleton />
                  </div>
                ))
                : filteredCourses.map((course) => (
                  <div
                    key={course.id}
                    className="shrink-0 w-[330px] md:w-[calc((100%-64px)/3)] md:max-w-[calc((100%-64px)/3)] snap-center md:snap-start py-4"
                  >
                    <CourseCard
                      course={{
                        ...course,
                        name: course?.course?.name ? `${course.name}: ${course?.course?.name}` : course.name,
                      }}
                    />
                  </div>
                ))}
            </div>

            {!loading && filteredCourses.length > 3 && (
            <button
              type="button"
              onClick={() => handleScroll('right')}
              disabled={!canScrollRight}
              className={`hidden md:inline-flex absolute -right-4 top-1/2 -translate-y-1/2 shadow p-3 z-10 rounded-lg ${
                canScrollRight ? 'bg-[#0267b1]' : 'bg-white'
              }`}
            >
              <Image
                src={canScrollRight ? ArrowRight : GreyNext}
                alt="Right Arrow"
                className="w-4 h-4"
              />
            </button>
            )}
            {!loading && filteredCourses.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => handleScroll('left')}
                disabled={!canScrollLeft}
                className={`absolute -left-3 top-1/2 -translate-y-1/2 p-3 md:hidden shadow z-10 rounded-lg ${
                  canScrollLeft ? 'bg-[#0267b1]' : 'bg-white'
                }`}
              >
                <Image
                  src={canScrollLeft ? ArrowRight : GreyNext}
                  alt="Left Arrow"
                  className="w-4 h-4 rotate-180"
                />
              </button>
              <button
                type="button"
                onClick={() => handleScroll('right')}
                disabled={!canScrollRight}
                className={`absolute -right-3 top-1/2 -translate-y-1/2 p-3 md:hidden shadow z-10 rounded-lg ${
                  canScrollRight ? 'bg-[#0267b1]' : 'bg-white'
                }`}
              >
                <Image
                  src={canScrollRight ? ArrowRight : GreyNext}
                  alt="Right Arrow"
                  className="w-4 h-4"
                />
              </button>
            </>
            )}
          </div>
        </div>

        {notes && filteredCourses.length > 0 && (
        <div className="bg-white rounded border p-3 sm:p-4 mt-2">
          <div
            className="prose prose-sm max-w-none notes-content text-black"
            dangerouslySetInnerHTML={{ __html: notes }}
          />
        </div>
        )}
      </div>
    </div>
  );
}

CourseBatchSection.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  sectionTitle: PropTypes.string,
  className: PropTypes.string,
  courses: PropTypes.array,
  // combos: PropTypes.array,
  notes: PropTypes.string,
  filterList: PropTypes.array,
  tagList: PropTypes.array,
  sectionId: PropTypes.string,
  courseGroupId: PropTypes.string.isRequired,
};
